// File: frontend/src/components/admin/AdminManual.tsx
//
// "Kullanım Kılavuzu" tab — a living reference for the whole platform.
//
// Why the numbers are not hardcoded:
//   Every admin-configurable value is read live from GET /api/admin/settings and
//   injected into the prose via {{setting:key}} tokens, and rendered as tables by
//   the "settings" block type. Changing a value in the Settings tab and reloading
//   this page immediately updates the manual, so the documentation can never drift
//   from the actual configuration.
//
// Access note:
//   GET /api/admin/settings is ADMIN only. For MODERATOR the request fails with
//   403; the manual still renders fully, with a small banner explaining that live
//   values are unavailable and setting tokens rendered as "—".

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    FaBook,
    FaSitemap,
    FaHashtag,
    FaFileAlt,
    FaUserShield,
    FaUserClock,
    FaUserSlash,
    FaUserGraduate,
    FaThumbsUp,
    FaBell,
    FaIdCard,
    FaSlidersH,
    FaComments,
    FaCircle,
    FaFlag,
    FaBan,
    FaCogs,
    FaFutbol,
    FaDatabase,
    FaRandom,
    FaThumbtack,
    FaLock,
    FaTrash,
    FaSearch,
    FaImage,
    FaHistory,
    FaTools,
    FaGavel,
    FaChevronDown,
    FaSpinner,
    FaTimes,
    FaExclamationTriangle,
    FaInfoCircle,
    FaCheckCircle,
    FaAngleDoubleDown,
    FaAngleDoubleUp,
    FaPrint,
} from "react-icons/fa";
import {
    getAllSettings,
    SystemSettingDto,
    toValueMap,
} from "../../services/adminSettingsService";
import { describeCron } from "../../utils/cronUtils";
import type { Translate } from "../../utils/cronUtils";
import {
    ALL_MANUAL_SECTIONS,
    MANUAL_CONSTANTS,
    MANUAL_GROUPS,
    ManualBlock,
    ManualIconKey,
    ManualLang,
    ManualSection,
    describeSetting,
} from "./adminManualContent";
import "./AdminManual.css";

interface AdminManualProps {
    isAdmin: boolean;
}

// ── Icon resolution ───────────────────────────────────────────────────────────

const ICONS: Record<ManualIconKey, React.ReactNode> = {
    book: <FaBook />,
    sitemap: <FaSitemap />,
    hashtag: <FaHashtag />,
    fileAlt: <FaFileAlt />,
    userShield: <FaUserShield />,
    userClock: <FaUserClock />,
    userSlash: <FaUserSlash />,
    graduation: <FaUserGraduate />,
    thumbsUp: <FaThumbsUp />,
    bell: <FaBell />,
    idCard: <FaIdCard />,
    sliders: <FaSlidersH />,
    comments: <FaComments />,
    circle: <FaCircle />,
    flag: <FaFlag />,
    ban: <FaBan />,
    cogs: <FaCogs />,
    futbol: <FaFutbol />,
    database: <FaDatabase />,
    random: <FaRandom />,
    thumbtack: <FaThumbtack />,
    lock: <FaLock />,
    trash: <FaTrash />,
    search: <FaSearch />,
    image: <FaImage />,
    history: <FaHistory />,
    tools: <FaTools />,
    gavel: <FaGavel />,
};

const NOTE_ICONS = {
    info: <FaInfoCircle />,
    ok: <FaCheckCircle />,
    warn: <FaExclamationTriangle />,
    danger: <FaExclamationTriangle />,
};

// ── Token interpolation ───────────────────────────────────────────────────────

/**
 * Replace {{setting:key}} with the live system-setting value and {{const:key}}
 * with the mirrored backend constant. Unknown keys render as an em dash so a
 * missing value is visible rather than silently wrong.
 */
const interpolate = (text: string, settings: Record<string, string>): string =>
    text
        .replace(/\{\{setting:([\w.]+)\}\}/g, (_m, key: string) => {
            const value = settings[key];
            return value !== undefined && value !== "" ? value : "—";
        })
        .replace(/\{\{const:(\w+)\}\}/g, (_m, key: string) => MANUAL_CONSTANTS[key] ?? "—");

/**
 * Render a string that may contain the small set of inline tags used by the
 * manual copy (b, i, code) plus interpolated tokens.
 *
 * The content is a compile-time constant authored in this repository — it is
 * never user input — so dangerouslySetInnerHTML is safe here. Keeping it as
 * markup avoids a parser for four tags.
 */
const RichText: React.FC<{ text: string; settings: Record<string, string> }> = ({
    text,
    settings,
}) => <span dangerouslySetInnerHTML={{ __html: interpolate(text, settings) }} />;

// ── Live settings table block ─────────────────────────────────────────────────

interface SettingsTableProps {
    keys: string[];
    settingRows: Record<string, SystemSettingDto>;
    available: boolean;
    lang: ManualLang;
}

const SettingsTable: React.FC<SettingsTableProps> = ({
    keys,
    settingRows,
    available,
    lang,
}) => {
    const headers =
        lang === "tr"
            ? ["Ayar anahtarı", "Güncel değer", "Açıklama", "Son değiştiren"]
            : ["Setting key", "Current value", "Description", "Last changed by"];

    return (
        <div className="admin-manual__table-wrap">
            <table className="admin-manual__table admin-manual__table--settings">
                <thead>
                    <tr>
                        {headers.map((h) => (
                            <th key={h}>{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {keys.map((key) => {
                        const row = settingRows[key];
                        const rawValue = row?.value ?? "";
                        // "true"/"false" are stored as raw strings — show them in
                        // the reader's language instead of leaking the raw value.
                        const value =
                            rawValue === "true"
                                ? lang === "tr" ? "Açık" : "On"
                                : rawValue === "false"
                                    ? lang === "tr" ? "Kapalı" : "Off"
                                    : rawValue;
                        const isEmpty = rawValue === "";
                        return (
                            <tr key={key}>
                                <td>
                                    <code className="admin-manual__key">{key}</code>
                                </td>
                                <td>
                                    {!available ? (
                                        <span className="admin-manual__muted">—</span>
                                    ) : isEmpty ? (
                                        <span className="admin-manual__muted">
                                            {lang === "tr" ? "(boş)" : "(empty)"}
                                        </span>
                                    ) : (
                                        <span className="admin-manual__value">{value}</span>
                                    )}
                                </td>
                                <td className="admin-manual__desc">
                                    {describeSetting(key, lang, row?.description)}
                                </td>
                                <td className="admin-manual__muted">
                                    {row?.updatedByUsername ?? "—"}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

// ── Block renderer ────────────────────────────────────────────────────────────

interface BlockProps {
    block: ManualBlock;
    settings: Record<string, string>;
    settingRows: Record<string, SystemSettingDto>;
    settingsAvailable: boolean;
    lang: ManualLang;
}

const Block: React.FC<BlockProps> = ({
    block,
    settings,
    settingRows,
    settingsAvailable,
    lang,
}) => {
    switch (block.type) {
        case "p":
            return (
                <p className="admin-manual__p">
                    <RichText text={block.text} settings={settings} />
                </p>
            );

        case "sub":
            return (
                <h4 className="admin-manual__sub">
                    <RichText text={block.text} settings={settings} />
                </h4>
            );

        case "ul":
            return (
                <ul className="admin-manual__list">
                    {block.items.map((item, i) => (
                        <li key={i}>
                            <RichText text={item} settings={settings} />
                        </li>
                    ))}
                </ul>
            );

        case "ol":
            return (
                <ol className="admin-manual__list admin-manual__list--ordered">
                    {block.items.map((item, i) => (
                        <li key={i}>
                            <RichText text={item} settings={settings} />
                        </li>
                    ))}
                </ol>
            );

        case "note":
            return (
                <div className={`admin-manual__note admin-manual__note--${block.tone}`}>
                    <span className="admin-manual__note-icon">{NOTE_ICONS[block.tone]}</span>
                    <span>
                        <RichText text={block.text} settings={settings} />
                    </span>
                </div>
            );

        case "table":
            return (
                <div className="admin-manual__table-wrap">
                    <table className="admin-manual__table">
                        <thead>
                            <tr>
                                {block.headers.map((h, i) => (
                                    <th key={i}>
                                        <RichText text={h} settings={settings} />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {block.rows.map((row, ri) => (
                                <tr key={ri}>
                                    {row.map((cell, ci) => (
                                        <td key={ci}>
                                            <RichText text={cell} settings={settings} />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );

        case "timeline":
            return (
                <div className="admin-manual__timeline">
                    {block.caption && (
                        <p className="admin-manual__timeline-caption">
                            <RichText text={block.caption} settings={settings} />
                        </p>
                    )}
                    <ol className="admin-manual__timeline-list">
                        {block.steps.map((step, i) => (
                            <li
                                key={i}
                                className={`admin-manual__timeline-step admin-manual__timeline-step--${step.tone ?? "info"}`}
                            >
                                <span className="admin-manual__timeline-at">
                                    <RichText text={step.at} settings={settings} />
                                </span>
                                <span className="admin-manual__timeline-body">
                                    <span className="admin-manual__timeline-label">
                                        <RichText text={step.label} settings={settings} />
                                    </span>
                                    <span className="admin-manual__timeline-text">
                                        <RichText text={step.text} settings={settings} />
                                    </span>
                                </span>
                            </li>
                        ))}
                    </ol>
                </div>
            );

        case "settings":
            return (
                <SettingsTable
                    keys={block.keys}
                    settingRows={settingRows}
                    available={settingsAvailable}
                    lang={lang}
                />
            );

        default:
            return null;
    }
};

// ── Section renderer ──────────────────────────────────────────────────────────

interface SectionProps {
    section: ManualSection;
    lang: ManualLang;
    open: boolean;
    onToggle: (id: string) => void;
    settings: Record<string, string>;
    settingRows: Record<string, SystemSettingDto>;
    settingsAvailable: boolean;
}

const Section: React.FC<SectionProps> = ({
    section,
    lang,
    open,
    onToggle,
    settings,
    settingRows,
    settingsAvailable,
}) => (
    <section
        className={`admin-manual__section ${open ? "is-open" : ""}`}
        id={`manual-${section.id}`}
    >
        <button
            type="button"
            className="admin-manual__section-head"
            onClick={() => onToggle(section.id)}
            aria-expanded={open}
        >
            <span className="admin-manual__section-icon">{ICONS[section.icon]}</span>
            <span className="admin-manual__section-titles">
                <span className="admin-manual__section-title">{section.title[lang]}</span>
                <span className="admin-manual__section-summary">{section.summary[lang]}</span>
            </span>
            <FaChevronDown className="admin-manual__chevron" />
        </button>

        {open && (
            <div className="admin-manual__section-body">
                {section.blocks[lang].map((block, i) => (
                    <Block
                        key={i}
                        block={block}
                        settings={settings}
                        settingRows={settingRows}
                        settingsAvailable={settingsAvailable}
                        lang={lang}
                    />
                ))}
            </div>
        )}
    </section>
);

// ── Main component ────────────────────────────────────────────────────────────

export const AdminManual: React.FC<AdminManualProps> = ({ isAdmin }) => {
    const { t, i18n } = useTranslation();

    // Manual copy only exists in tr/en; anything else falls back to English.
    const lang: ManualLang = i18n.language?.startsWith("tr") ? "tr" : "en";

    const [settingRows, setSettingRows] = useState<Record<string, SystemSettingDto>>({});
    const [settings, setSettings] = useState<Record<string, string>>({});
    const [settingsAvailable, setSettingsAvailable] = useState(false);
    const [loading, setLoading] = useState(true);

    const [query, setQuery] = useState("");
    const [openIds, setOpenIds] = useState<Set<string>>(
        // The first section starts expanded so the page never looks empty.
        () => new Set([ALL_MANUAL_SECTIONS[0]?.id].filter(Boolean) as string[])
    );

    // ── Load live settings ────────────────────────────────────────────────────
    const loadSettings = useCallback(async () => {
        setLoading(true);
        try {
            const rows = await getAllSettings();
            const byKey: Record<string, SystemSettingDto> = {};
            rows.forEach((r) => {
                byKey[r.key] = r;
            });
            setSettingRows(byKey);
            setSettings(toValueMap(rows));
            setSettingsAvailable(true);
        } catch {
            // MODERATOR gets 403 here — the manual still renders, tokens show "—"
            setSettingsAvailable(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    // ── Derived setting values ────────────────────────────────────────────────
    // The manual quotes schedules in prose, and a raw cron expression means
    // nothing to a reader. Rather than teach the interpolator a second token
    // type, the readable form is added to the map under a "<key>.human" name, so
    // the copy can keep using the ordinary {{setting:...}} token.
    //
    // These have to be derived, not hard-coded: the cron expressions are now
    // admin-editable, so a fixed "03:00 or 15:00" in the copy would start lying
    // the moment anyone changed the schedule.
    const manualSettings = useMemo<Record<string, string>>(() => {
        const derived: Record<string, string> = { ...settings };
        const zone = settings["scheduler.timezone"] || "UTC";

        Object.keys(settings)
            .filter((key) => key.startsWith("scheduler.") && key.endsWith(".cron"))
            .forEach((key) => {
                const readable = describeCron(settings[key], t as unknown as Translate);
                derived[`${key}.human`] = `${readable} (${zone})`;
            });

        return derived;
    }, [settings, t]);

    // ── Search filter ─────────────────────────────────────────────────────────
    // Matches against title, summary and the raw text of every block, so a term
    // like "nickaltı" or "merge" finds the section that actually discusses it.
    const matchedIds = useMemo<Set<string> | null>(() => {
        const q = query.trim().toLocaleLowerCase(lang === "tr" ? "tr" : "en");
        if (q.length < 2) return null;

        const haystackOf = (section: ManualSection): string => {
            const parts: string[] = [section.title[lang], section.summary[lang]];
            section.blocks[lang].forEach((b) => {
                switch (b.type) {
                    case "p":
                    case "sub":
                        parts.push(b.text);
                        break;
                    case "note":
                        parts.push(b.text);
                        break;
                    case "ul":
                    case "ol":
                        parts.push(b.items.join(" "));
                        break;
                    case "table":
                        parts.push(b.headers.join(" "));
                        b.rows.forEach((r) => parts.push(r.join(" ")));
                        break;
                    case "timeline":
                        // Include the whole axis so a search for "askı" or "grace"
                        // finds the timeline section, not just the prose around it
                        if (b.caption) parts.push(b.caption);
                        b.steps.forEach((st) => parts.push(st.at, st.label, st.text));
                        break;
                    case "settings":
                        parts.push(b.keys.join(" "));
                        break;
                    default:
                        break;
                }
            });
            return parts.join(" ").toLocaleLowerCase(lang === "tr" ? "tr" : "en");
        };

        const ids = new Set<string>();
        ALL_MANUAL_SECTIONS.forEach((s) => {
            if (haystackOf(s).includes(q)) ids.add(s.id);
        });
        return ids;
    }, [query, lang]);

    const isSearching = matchedIds !== null;
    const matchCount = matchedIds?.size ?? 0;

    // While searching, matching sections auto-expand so results are readable.
    const isOpen = useCallback(
        (id: string) => (isSearching ? true : openIds.has(id)),
        [isSearching, openIds]
    );

    const toggleSection = useCallback((id: string) => {
        setOpenIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const expandAll = useCallback(() => {
        setOpenIds(new Set(ALL_MANUAL_SECTIONS.map((s) => s.id)));
    }, []);

    const collapseAll = useCallback(() => {
        setOpenIds(new Set());
    }, []);

    const scrollToSection = useCallback((id: string) => {
        setOpenIds((prev) => new Set(prev).add(id));
        // Defer until the section body has been expanded and laid out
        window.setTimeout(() => {
            document
                .getElementById(`manual-${id}`)
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 60);
    }, []);

    const visibleGroups = useMemo(
        () =>
            MANUAL_GROUPS.map((g) => ({
                ...g,
                sections: isSearching
                    ? g.sections.filter((s) => matchedIds!.has(s.id))
                    : g.sections,
            })).filter((g) => g.sections.length > 0),
        [isSearching, matchedIds]
    );

    return (
        <div className="admin-manual">
            {/* ── Header ─────────────────────────────────────────────────── */}
            <div className="admin-manual__header">
                <div>
                    <h2 className="admin-manual__title">
                        <FaBook />
                        {lang === "tr" ? "Kullanım Kılavuzu" : "Usage Manual"}
                    </h2>
                    <p className="admin-manual__lead">
                        {lang === "tr"
                            ? "Platformun tüm kuralları tek sayfada. Ayarlarla yönetilen sayılar bu sayfada canlı olarak gösterilir — Ayarlar sekmesinde bir değeri değiştirdiğinizde kılavuz da güncellenir."
                            : "Every platform rule on one page. Admin-configurable numbers are shown live — change a value in the Settings tab and this manual updates with it."}
                    </p>
                </div>
                <div className="admin-manual__header-actions">
                    <button type="button" className="admin-manual__btn" onClick={expandAll}>
                        <FaAngleDoubleDown />
                        {lang === "tr" ? "Tümünü aç" : "Expand all"}
                    </button>
                    <button type="button" className="admin-manual__btn" onClick={collapseAll}>
                        <FaAngleDoubleUp />
                        {lang === "tr" ? "Tümünü kapat" : "Collapse all"}
                    </button>
                    <button
                        type="button"
                        className="admin-manual__btn"
                        onClick={() => window.print()}
                    >
                        <FaPrint />
                        {lang === "tr" ? "Yazdır" : "Print"}
                    </button>
                </div>
            </div>

            {/* ── Status banners ─────────────────────────────────────────── */}
            {loading && (
                <div className="admin-manual__banner admin-manual__banner--info">
                    <FaSpinner className="admin-manual__spin" />
                    {lang === "tr"
                        ? "Güncel ayar değerleri yükleniyor…"
                        : "Loading live setting values…"}
                </div>
            )}

            {!loading && !settingsAvailable && (
                <div className="admin-manual__banner admin-manual__banner--warn">
                    <FaExclamationTriangle />
                    {isAdmin
                        ? lang === "tr"
                            ? "Ayar değerleri okunamadı. Kurallar eksiksiz görünür, sayısal değerler “—” olarak gösterilir."
                            : "Setting values could not be loaded. The rules are complete; numeric values show as “—”."
                        : lang === "tr"
                            ? "Canlı ayar değerleri yalnızca ADMIN rolüne açıktır. Kurallar eksiksiz görünür, sayısal değerler “—” olarak gösterilir."
                            : "Live setting values are ADMIN only. The rules are complete; numeric values show as “—”."}
                </div>
            )}

            <div className="admin-manual__layout">
                {/* ── Navigation rail ────────────────────────────────────── */}
                <nav className="admin-manual__nav">
                    <div className="admin-manual__search">
                        <FaSearch className="admin-manual__search-icon" />
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={
                                lang === "tr" ? "Kılavuzda ara…" : "Search the manual…"
                            }
                            className="admin-manual__search-input"
                        />
                        {query && (
                            <button
                                type="button"
                                className="admin-manual__search-clear"
                                onClick={() => setQuery("")}
                                aria-label={lang === "tr" ? "Temizle" : "Clear"}
                            >
                                <FaTimes />
                            </button>
                        )}
                    </div>

                    {isSearching && (
                        <div className="admin-manual__match-count">
                            {lang === "tr"
                                ? `${matchCount} bölüm eşleşti`
                                : `${matchCount} section(s) matched`}
                        </div>
                    )}

                    {visibleGroups.map((group) => (
                        <div key={group.id} className="admin-manual__nav-group">
                            <div className="admin-manual__nav-group-title">
                                {group.title[lang]}
                            </div>
                            {group.sections.map((section) => (
                                <button
                                    key={section.id}
                                    type="button"
                                    className="admin-manual__nav-item"
                                    onClick={() => scrollToSection(section.id)}
                                >
                                    <span className="admin-manual__nav-icon">
                                        {ICONS[section.icon]}
                                    </span>
                                    {section.title[lang]}
                                </button>
                            ))}
                        </div>
                    ))}
                </nav>

                {/* ── Content ────────────────────────────────────────────── */}
                <div className="admin-manual__content">
                    {visibleGroups.length === 0 && (
                        <div className="admin-manual__empty">
                            {lang === "tr"
                                ? "Aramanızla eşleşen bir bölüm bulunamadı."
                                : "No section matched your search."}
                        </div>
                    )}

                    {visibleGroups.map((group) => (
                        <div key={group.id} className="admin-manual__group">
                            <h3 className="admin-manual__group-title">{group.title[lang]}</h3>
                            {group.sections.map((section) => (
                                <Section
                                    key={section.id}
                                    section={section}
                                    lang={lang}
                                    open={isOpen(section.id)}
                                    onToggle={toggleSection}
                                    settings={manualSettings}
                                    settingRows={settingRows}
                                    settingsAvailable={settingsAvailable}
                                />
                            ))}
                        </div>
                    ))}

                    <div className="admin-manual__footer">
                        {lang === "tr"
                            ? "Bu kılavuz koddaki kurallara göre hazırlanmıştır. Bir davranış burada yazandan farklıysa, önce ilgili ayarı kontrol edin; ayar da uyuyorsa bu bir hatadır."
                            : "This manual mirrors the rules in the code. If behaviour differs from what you read here, check the relevant setting first; if the setting matches, it is a bug."}
                    </div>
                </div>
            </div>

            {/* Referenced so the shared translation namespace stays wired up
                even though the manual copy itself is not translated via i18next. */}
            <span className="admin-manual__sr-only">{t("admin_panel_title")}</span>
        </div>
    );
};